import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { organiserQueries } from "../features/organisers/api/queries";
import { venueQueries } from "../features/venues/api/queries";
import { areaQueries } from "../features/weather/api/queries";
import { useOffset } from "../shared/lib/paging";
import { AppHeader } from "../shared/ui/AppHeader";
import { Pager } from "../shared/ui/Pager";

/** How many organisers a page holds. Each row carries images and links. */
const PAGE_SIZE = 20;

export const Route = createFileRoute("/_authenticated/organisers/")({
  loader: ({ context: { queryClient } }) =>
    /*
     * Venues and areas alongside the list: this page reads them to name each
     * organiser's usual location, and the add/edit dialogs that open over it
     * need them for their pickers.
     */
    Promise.all([
      queryClient.ensureQueryData(organiserQueries.page({ limit: PAGE_SIZE })),
      queryClient.ensureQueryData(venueQueries.list()),
      queryClient.ensureQueryData(areaQueries.list()),
    ]),
  component: OrganisersPage,
  errorComponent: ({ error }) => (
    <main className="p-6">
      <p role="alert" className="text-brand">
        Couldn’t load organisers: {error.message}
      </p>
    </main>
  ),
});

/**
 * The organiser list. Adding and editing happen in a dialog over it, at
 * `/organisers/new` and `/organisers/$organiserId/edit`.
 *
 * No delete, deliberately: `event_organisers.organiserId` is a real FK, so the
 * server exposes create and update only.
 */
function OrganisersPage() {
  const page = useOffset(PAGE_SIZE);
  const { data } = useSuspenseQuery(
    organiserQueries.page({ limit: PAGE_SIZE, offset: page.offset }),
  );

  const organisers = data.organiserPage.items;
  const total = data.organiserPage.totalCount;
  page.reconcile(total);
  const { data: venueData } = useSuspenseQuery(venueQueries.list());
  const { data: areaData } = useSuspenseQuery(areaQueries.list());

  const venueName = (id: string | null) =>
    venueData.venues.find((venue) => venue.id === id)?.name;
  const areaName = (id: string | null) =>
    areaData.areas.find((area) => area.id === id)?.name;

  return (
    <>
      <AppHeader />
      <main className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Organisers</h1>
            <p className="mt-1 text-sm text-content-muted">
              Who puts events on. Shared across everyone’s events, like venues —
              correct one if it’s wrong.
            </p>
          </div>
          <Link
            to="/organisers/new"
            className="rounded-md bg-brand px-4 py-2 font-medium text-brand-contrast hover:bg-brand-hover"
          >
            Add organiser
          </Link>
        </div>

        <div className="mt-8">
          {organisers.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border-subtle p-10 text-center">
              <p className="font-medium">No organisers yet</p>
              <p className="mt-1 text-sm text-content-muted">
                Add the first one to attribute events to it.
              </p>
              <Link
                to="/organisers/new"
                className="mt-4 inline-block rounded-md bg-brand px-4 py-2 font-medium text-brand-contrast hover:bg-brand-hover"
              >
                Add organiser
              </Link>
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {organisers.map((organiser) => {
                const primary =
                  organiser.links.find((link) => link.isPrimary) ??
                  organiser.links[0];

                return (
                  <li
                    key={organiser.id}
                    className="flex items-start justify-between gap-4 rounded-lg border border-border-subtle bg-surface p-3"
                  >
                    {/* Primary first, else whatever the server ordered first. */}
                    {(() => {
                      const cover =
                        organiser.images.find((image) => image.isPrimary) ??
                        organiser.images[0];
                      return cover ? (
                        <img
                          src={cover.thumbnailUrl}
                          alt={cover.altText ?? ""}
                          className="h-14 w-14 shrink-0 rounded object-cover"
                        />
                      ) : null;
                    })()}

                    <div className="mr-auto min-w-0">
                      <p className="font-medium">
                        {/*
                          The row keeps its Edit control for the fields the
                          dialog owns; the name opens the detail page, which is
                          where registration and everything attached to the
                          organiser live.
                        */}
                        <Link
                          to="/organisers/$organiserId"
                          params={{ organiserId: organiser.id }}
                          className="hover:underline"
                        >
                          {organiser.name}
                        </Link>
                      </p>
                      {organiser.description && (
                        <p className="text-sm text-content-muted">
                          {organiser.description}
                        </p>
                      )}
                      <p className="text-xs text-content-muted">
                        {organiser.defaultVenueId
                          ? `Usually at ${venueName(organiser.defaultVenueId) ?? "a venue that no longer exists"}`
                          : organiser.defaultAreaId
                            ? `Usually around ${areaName(organiser.defaultAreaId) ?? "an area that no longer exists"}`
                            : "No usual location"}
                      </p>
                      {primary && (
                        <a
                          href={primary.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="mt-1 block break-all text-xs text-content-muted underline hover:text-content"
                        >
                          {primary.label ?? primary.url}
                          {primary.isPrimary ? "" : " (no primary set)"}
                        </a>
                      )}
                    </div>
                    <Link
                      to="/organisers/$organiserId/edit"
                      params={{ organiserId: organiser.id }}
                      className="shrink-0 rounded-md border border-border-subtle px-3 py-1 text-sm hover:bg-surface-muted"
                    >
                      Edit
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}

          <Pager
            offset={page.offset}
            limit={PAGE_SIZE}
            totalCount={total}
            onOffsetChange={page.setOffset}
            noun="organisers"
          />
        </div>
      </main>
    </>
  );
}
